import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ExperienceCard from "@/components/ExperienceCard";
import VibeChips from "@/components/VibeChips";
import MapView from "@/components/MapView";
import { Input } from "@/components/ui/input";
import { Search, LayoutGrid, Map as MapIcon, X as XIcon } from "lucide-react";

const PILLARS = [
  { slug: "", label: "All" },
  { slug: "spirituality", label: "Yoga" },
  { slug: "work", label: "Working" },
  { slug: "activities", label: "Learning" },
  { slug: "sharing", label: "Sharing" },
];

// Multi-select category filter — matches backend CATEGORIES.
const CATEGORIES = [
  { slug: "yoga", label: "Yoga" },
  { slug: "working-space", label: "Working Space" },
  { slug: "wildlife", label: "Wildlife" },
  { slug: "local-experience", label: "Local Experience" },
  { slug: "expert-skill-sharing", label: "Expert/Skill Sharing" },
  { slug: "community-connection", label: "Community Connection" },
];

export default function Discover() {
  const [params, setParams] = useSearchParams();
  const [experiences, setExperiences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState(params.get("search") || "");
  const [minPrice, setMinPrice] = useState(params.get("min_price") || "");
  const [maxPrice, setMaxPrice] = useState(params.get("max_price") || "");
  const [minDays, setMinDays] = useState(params.get("min_days") || "");
  const [maxDays, setMaxDays] = useState(params.get("max_days") || "");
  const [view, setView] = useState(params.get("view") || "grid");
  const pillar = params.get("pillar") || "";
  const persona = params.get("persona") || "";
  const vibe = params.get("vibe") || "";
  const selectedCategories = params.getAll("category");

  useEffect(() => {
    setLoading(true);
    const usp = new URLSearchParams();
    if (pillar) usp.set("pillar", pillar);
    if (persona) usp.set("persona", persona);
    if (vibe) usp.set("vibe", vibe);
    for (const p of ["search", "min_price", "max_price", "min_days", "max_days"]) {
      const v = params.get(p);
      if (v) usp.set(p, v);
    }
    selectedCategories.forEach((c) => usp.append("category", c));
    api.get(`/experiences?${usp.toString()}`)
      .then((r) => setExperiences(r.data))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pillar, persona, vibe, params]);

  const setPillar = (slug) => {
    const next = new URLSearchParams(params);
    if (slug) next.set("pillar", slug); else next.delete("pillar");
    setParams(next);
  };

  const toggleCategory = (slug) => {
    const next = new URLSearchParams(params);
    const current = next.getAll("category");
    next.delete("category");
    let updated;
    if (current.includes(slug)) {
      updated = current.filter((c) => c !== slug);
    } else {
      updated = [...current, slug];
    }
    updated.forEach((c) => next.append("category", c));
    setParams(next);
  };

  const clearAllCategories = () => {
    const next = new URLSearchParams(params);
    next.delete("category");
    setParams(next);
  };

  const setVibe = (id) => {
    const next = new URLSearchParams(params);
    if (id) next.set("vibe", id); else next.delete("vibe");
    setParams(next);
  };

  const setViewMode = (v) => {
    setView(v);
    const next = new URLSearchParams(params);
    if (v === "map") next.set("view", "map"); else next.delete("view");
    setParams(next);
  };

  const submitSearch = (e) => {
    e.preventDefault();
    const next = new URLSearchParams(params);
    if (query) next.set("search", query); else next.delete("search");
    setParams(next);
  };

  const applyFilters = () => {
    const next = new URLSearchParams(params);
    for (const [k, v] of [["min_price", minPrice], ["max_price", maxPrice], ["min_days", minDays], ["max_days", maxDays]]) {
      if (v) next.set(k, v); else next.delete(k);
    }
    setParams(next);
  };

  const clearFilters = () => {
    setMinPrice(""); setMaxPrice(""); setMinDays(""); setMaxDays("");
    const next = new URLSearchParams();
    if (pillar) next.set("pillar", pillar);
    setParams(next);
  };

  return (
    <div className="min-h-screen bg-canvas">
      <Navbar />
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-10 pb-8">
        <div className="overline mb-4">Discover</div>
        <h1 className="font-serif text-4xl md:text-6xl tracking-tight leading-none">
          Find your <span className="italic text-moss">next becoming</span>.
        </h1>

        <form onSubmit={submitSearch} className="mt-8 max-w-xl relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted2" />
          <Input
            data-testid="discover-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by place, host, or feeling…"
            className="pl-11 rounded-full bg-white border-subtle h-12"
          />
        </form>

        <div className="flex flex-wrap gap-2 mt-6">
          {PILLARS.map((p) => (
            <button
              key={p.slug || "all"}
              data-testid={`filter-pillar-${p.slug || "all"}`}
              onClick={() => setPillar(p.slug)}
              className={`rounded-full px-5 py-2 text-sm border transition-colors ${
                pillar === p.slug
                  ? "bg-moss text-canvas border-moss"
                  : "bg-white border-subtle hover:border-moss"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Multi-select category filter */}
        <div className="mt-6" data-testid="category-filter">
          <div className="flex items-baseline justify-between mb-2">
            <div className="overline">Categories</div>
            {selectedCategories.length > 0 && (
              <button
                data-testid="clear-all-categories"
                onClick={clearAllCategories}
                className="inline-flex items-center gap-1 text-xs text-muted2 hover:text-moss transition-colors"
                type="button"
              >
                <XIcon className="h-3 w-3" /> Clear all ({selectedCategories.length})
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => {
              const active = selectedCategories.includes(c.slug);
              return (
                <button
                  key={c.slug}
                  data-testid={`filter-category-${c.slug}`}
                  aria-pressed={active}
                  onClick={() => toggleCategory(c.slug)}
                  type="button"
                  className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm border transition-colors ${
                    active
                      ? "bg-moss text-canvas border-moss"
                      : "bg-white border-subtle hover:border-moss"
                  }`}
                >
                  {active && <XIcon className="h-3 w-3" />}
                  {c.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-6">
          <div className="overline mb-2">Vibes</div>
          <VibeChips selected={vibe} onSelect={setVibe} />
        </div>

        {persona && (
          <div className="mt-4 text-sm text-muted2">
            Filtered by persona: <span className="text-moss font-medium">{persona}</span>
          </div>
        )}

        <div className="mt-6 bg-white border border-subtle rounded-2xl p-5 grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
          <div>
            <label className="text-xs text-muted2 block mb-1">Min ₹</label>
            <Input data-testid="filter-min-price" type="number" value={minPrice} onChange={(e) => setMinPrice(e.target.value)} placeholder="0" className="rounded-xl bg-canvas border-subtle" />
          </div>
          <div>
            <label className="text-xs text-muted2 block mb-1">Max ₹</label>
            <Input data-testid="filter-max-price" type="number" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} placeholder="2000" className="rounded-xl bg-canvas border-subtle" />
          </div>
          <div>
            <label className="text-xs text-muted2 block mb-1">Min days</label>
            <Input data-testid="filter-min-days" type="number" value={minDays} onChange={(e) => setMinDays(e.target.value)} placeholder="1" className="rounded-xl bg-canvas border-subtle" />
          </div>
          <div>
            <label className="text-xs text-muted2 block mb-1">Max days</label>
            <Input data-testid="filter-max-days" type="number" value={maxDays} onChange={(e) => setMaxDays(e.target.value)} placeholder="30" className="rounded-xl bg-canvas border-subtle" />
          </div>
          <div className="flex gap-2">
            <button data-testid="apply-filters" onClick={applyFilters} className="flex-1 rounded-full bg-moss text-canvas text-sm py-2.5 hover:bg-moss-hover transition-colors">Apply</button>
            <button data-testid="clear-filters" onClick={clearFilters} className="rounded-full bg-white border border-subtle text-sm px-4 py-2.5 hover:border-moss transition-colors">Clear</button>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-24">
        <div className="flex items-center justify-between mb-6 pt-2">
          <div className="text-sm text-muted2" data-testid="results-count">
            {loading ? "Loading…" : `${experiences.length} experience${experiences.length === 1 ? "" : "s"}`}
          </div>
          <div className="inline-flex rounded-full border border-subtle bg-white overflow-hidden">
            <button
              data-testid="view-grid"
              onClick={() => setViewMode("grid")}
              className={`inline-flex items-center gap-1.5 px-4 py-2 text-xs transition-colors ${view === "grid" ? "bg-moss text-canvas" : "text-muted2 hover:bg-subtle"}`}
              aria-pressed={view === "grid"}
            >
              <LayoutGrid className="h-3.5 w-3.5" /> Grid
            </button>
            <button
              data-testid="view-map"
              onClick={() => setViewMode("map")}
              className={`inline-flex items-center gap-1.5 px-4 py-2 text-xs transition-colors ${view === "map" ? "bg-moss text-canvas" : "text-muted2 hover:bg-subtle"}`}
              aria-pressed={view === "map"}
            >
              <MapIcon className="h-3.5 w-3.5" /> Map
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-24 text-muted2">Loading journeys…</div>
        ) : experiences.length === 0 ? (
          <div className="text-center py-24 text-muted2" data-testid="no-experiences">
            No experiences match yet. Try clearing categories, or picking a different pillar or vibe.
          </div>
        ) : view === "map" ? (
          <MapView experiences={experiences} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
            {experiences.map((e) => <ExperienceCard key={e.id} exp={e} />)}
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
